import React from 'react';
import classes from './Home12Services.module.scss';
import { Link } from 'react-router-dom';
import Home12Header from './Home12Header';

const Home12Services = () => {
    const heroRef = React.useRef(null);

    // Parallax & Scroll Reveal
    React.useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            const yOffset = window.scrollY;
            if (heroRef.current) {
                heroRef.current.style.backgroundPositionY = `${yOffset * 0.5}px`;
            }

            const parallaxEls = document.querySelectorAll('.js-parallax');
            parallaxEls.forEach(el => {
                const speed = parseFloat(el.getAttribute('data-speed')) || 0.15;
                el.style.transform = `translateY(${yOffset * speed}px)`;
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add(classes.visible);
                    obs.unobserve(entry.target);
                }
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

        const animEls = document.querySelectorAll(`.${classes.animate}`);
        animEls.forEach(el => observer.observe(el));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    const services = [
        {
            tag: "WORKFLOW",
            title: "現場目線の作業効率化・業務管理",
            desc: "日々変化する現場の課題にリアルタイムに対応。紙やExcelに依存していた業務をモバイル上で完結させ、誰でも迷わず操作できる直感的なインターフェースを提供します。管理者にとっても、現場の進捗が手に取るようにわかる透明性の高い管理機能が備わっています。",
            image: "/react-assets/home10/home10-card-dashboard-main.jpg",
            icon: "/react-assets/home10/icons/home10-icon-drag.png"
        },
        {
            tag: "AX / DX",
            title: "AIを活用した現場促進 (AX)",
            desc: "人の手に頼っていた分析や判断をAIがサポート。蓄積された行動データや業務データから、次に取るべき最適なアクションを自動提案します。経験や勘に頼らない、データ駆動型の現場運営を実現します。",
            image: "/react-assets/home10/home10-feature-analytics.jpg",
            icon: "/react-assets/home10/icons/home10-icon-preview.png"
        },
        {
            tag: "AUTOMATION",
            title: "ペーパーレス・帳簿自動化・決済",
            desc: "業務のボトルネックになりがちな事務手続きを極限まで自動化。見積もりから請求、オンライン決済までの流れをシームレスにつなぎ、ミスや業務負担を大幅に削減。リソースをより付加価値の高い業務へ集中させます。",
            image: "/react-assets/home10/home10-card-meeting.jpg",
            icon: "/react-assets/home10/icons/home10-icon-test.png"
        },
        {
            tag: "CRM",
            title: "SNS連携・顧客管理システム",
            desc: "ただ顧客情報を管理するだけでなく、SNS等との導線をシームレスに連携。見込み客との接点を強化し、リピーターへと育成する仕組みをパッケージ化して提供。売上の最大化に直接貢献します。",
            image: "/react-assets/home10/home10-card-dashboard-side.jpg",
            icon: "/react-assets/home10/icons/home10-icon-flow.png"
        }
    ];

    return (
        <div className={classes.root}>
            <Home12Header mode="sticky" />

            {/* Hero Section */}
            <section
                ref={heroRef}
                className={classes.pageHero}
                style={{
                    backgroundImage: "linear-gradient(180deg, rgba(8, 21, 39, 0.6) 0%, rgba(8, 21, 39, 0.8) 100%), url('/react-assets/home12/home12-services-hero-bg-portrait-gray.jpg')",
                    backgroundPosition: "center top"
                }}
            >
                <div className={classes.heroContent}>
                    <div className={`${classes.subLabel} ${classes.animate} ${classes.fadeInUp}`}>Services &amp; Features</div>
                    <h1 className={`${classes.heroTitle} ${classes.animate} ${classes.fadeInUp} ${classes.delay1}`}>
                        現場のチカラを解放する<br />
                        <span className={classes.gradientHighlight}>ソリューション</span>
                    </h1>
                    <p className={`${classes.heroDesc} ${classes.animate} ${classes.fadeInUp} ${classes.delay2}`}>
                        それぞれの業務課題にフィットする、シームレスで拡張性の高い機能群。collabo nexusが実現する具体的な価値をご紹介します。
                    </p>
                </div>
            </section>

            {/* Service Details Section */}
            <section className={classes.servicesWrap}>
                {services.map((svc, idx) => {
                    const isEven = idx % 2 !== 0;
                    return (
                        <div key={idx} className={`${classes.serviceRow} ${isEven ? classes.rowReverse : ''}`}>
                            <div className={`${classes.serviceImgBox} ${classes.animate} ${isEven ? classes.slideInRight : classes.slideInLeft}`}>
                                <div className={classes.imgWrapper}>
                                    <img src={svc.image} alt={svc.title} className="js-parallax" data-speed="0.03" />
                                </div>
                            </div>
                            <div className={`${classes.serviceTextBox} ${classes.animate} ${isEven ? classes.slideInLeft : classes.slideInRight} ${classes.delay1}`}>
                                <div className={classes.svcTag}>
                                    <img src={svc.icon} alt="" />
                                    {svc.tag}
                                </div>
                                <h2 className={classes.svcTitle}>{svc.title}</h2>
                                <p className={classes.svcDesc}>{svc.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </section>

            {/* Process Section */}
            <section className={classes.processBlock}>
                <div className={`${classes.sectionHeader} ${classes.animate} ${classes.fadeInUp}`}>
                    <h2 className={classes.sectionTitle}>導入フロー</h2>
                    <p className={classes.sectionSub}>ご相談から最短2週間でテスト環境をご提供</p>
                </div>
                <div className={classes.processSteps}>
                    {['ヒアリング・課題整理', '要件定義・プロトタイプ作成', 'システム構築・AI連携', 'テスト運用・導入サポート'].map((step, i) => (
                        <div key={i} className={`${classes.stepBox} ${classes.animate} ${classes.fadeInUp}`} style={{ transitionDelay: `${i * 150}ms` }}>
                            <div className={classes.stepNum}>STEP 0{i + 1}</div>
                            <h3>{step}</h3>
                        </div>
                    ))}
                </div>
            </section>

            {/* Bottom CTA */}
            <section className={classes.bottomCta}>
                <div className={`${classes.ctaContainer} ${classes.animate} ${classes.zoomIn}`}>
                    <h2>まずは、御社の課題をお聞かせください。</h2>
                    <div className={classes.ctaButtons}>
                        <Link to="/download" className={classes.btnPrimary}>無料で資料請求する</Link>
                        <Link to="/" className={classes.btnSecondary}>トップページへ戻る</Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={classes.footer}>
                <div className={classes.slogan}>Practical DX for Every Team</div>
                <div className={classes.footerLinks}>
                    <Link to="/company">運営会社</Link>
                    <Link to="/privacy">プライバシーポリシー</Link>
                    <a href="#contact">お問い合わせ</a>
                </div>
                <div className={classes.companyInfo}>
                    <div className={classes.name}>コラボ株式会社</div>
                    <p>
                        〒921-8163<br />
                        石川県金沢市横川７丁目２６<br />
                        TEL: 076-287-0026
                    </p>
                    <p style={{ marginTop: '10px' }}>&copy; {new Date().getFullYear()} collabo Inc.</p>
                </div>
            </footer>
        </div>
    );
};

export default Home12Services;
